/* ============================================
   USE READING PROGRESS HOOK
   The Creative Orchestra - GEMINI Framework
   Tracks scroll progress while reading novel
   ============================================ */

import { useState, useEffect, useCallback, useRef } from 'react';
import { throttle, getScrollPercentage } from '@utils/helpers';

/**
 * Custom hook for tracking reading progress
 * Monitors scroll position and calculates progress percentage
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.throttleMs - Throttle scroll events (default: 100ms)
 * @param {Function} options.onProgressChange - Callback when progress changes
 * @param {Function} options.onScrollEnd - Callback when scrolling stops
 * @param {number} options.scrollEndDelay - Delay before scroll end fires (default: 150ms)
 * @returns {Object} Progress state and utilities
 */
const useReadingProgress = (options = {}) => {
  const {
    throttleMs = 100,
    onProgressChange,
    onScrollEnd,
    scrollEndDelay = 150,
  } = options;

  // State
  const [progress, setProgress] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null); // 'up' | 'down' | null
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  // Refs
  const lastScrollTop = useRef(0);
  const scrollEndTimer = useRef(null);
  const containerRef = useRef(null);

  /**
   * Calculate progress based on scroll position
   * Uses either a container element or the window
   */
  const calculateProgress = useCallback(() => {
    let scrollTop, scrollHeight, clientHeight;

    if (containerRef.current) {
      // Use container element
      scrollTop = containerRef.current.scrollTop;
      scrollHeight = containerRef.current.scrollHeight;
      clientHeight = containerRef.current.clientHeight;
    } else {
      // Use window/document
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    }

    const maxScroll = scrollHeight - clientHeight;
    const percentage = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 0;

    return {
      percentage: Math.min(100, Math.max(0, percentage)),
      scrollTop,
      maxScroll,
    };
  }, []);

  /**
   * Handle scroll event
   */
  const handleScroll = useCallback(() => {
    const { percentage, scrollTop } = calculateProgress();

    // Determine scroll direction
    const direction = scrollTop > lastScrollTop.current ? 'down' : 'up';
    lastScrollTop.current = scrollTop;

    // Update state
    setProgress(percentage);
    setScrollPosition(scrollTop);
    setScrollDirection(direction);
    setIsScrolling(true);

    // Check if reached end (95% threshold)
    if (percentage >= 95 && !hasReachedEnd) {
      setHasReachedEnd(true);
    }

    // Callback
    if (onProgressChange) {
      onProgressChange({
        progress: percentage,
        scrollPosition: scrollTop,
        direction,
      });
    }

    // Clear existing timer
    if (scrollEndTimer.current) {
      clearTimeout(scrollEndTimer.current);
    }

    // Set scroll end timer
    scrollEndTimer.current = setTimeout(() => {
      setIsScrolling(false);
      setScrollDirection(null);

      if (onScrollEnd) {
        onScrollEnd({
          progress: percentage,
          scrollPosition: scrollTop,
        });
      }
    }, scrollEndDelay);
  }, [calculateProgress, hasReachedEnd, onProgressChange, onScrollEnd, scrollEndDelay]);

  /**
   * Throttled scroll handler
   */
  const throttledScrollHandler = useCallback(
    throttle(handleScroll, throttleMs),
    [handleScroll, throttleMs]
  );

  /**
   * Set up scroll listener
   */
  useEffect(() => {
    const target = containerRef.current || window;
    
    target.addEventListener('scroll', throttledScrollHandler, { passive: true });

    // Initial calculation
    handleScroll();

    return () => {
      target.removeEventListener('scroll', throttledScrollHandler);
      if (scrollEndTimer.current) {
        clearTimeout(scrollEndTimer.current);
      }
    };
  }, [throttledScrollHandler, handleScroll]);

  /**
   * Scroll to a specific percentage
   * @param {number} targetPercentage - Target percentage (0-100)
   * @param {boolean} smooth - Use smooth scrolling
   */
  const scrollToProgress = useCallback((targetPercentage, smooth = true) => {
    const target = containerRef.current || document.documentElement;
    const scrollHeight = target.scrollHeight;
    const clientHeight = containerRef.current 
      ? target.clientHeight 
      : window.innerHeight;
    
    const maxScroll = scrollHeight - clientHeight;
    const targetScroll = (targetPercentage / 100) * maxScroll;

    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: targetScroll,
        behavior: smooth ? 'smooth' : 'auto',
      });
    } else {
      window.scrollTo({
        top: targetScroll,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }, []);

  /**
   * Scroll to top
   * @param {boolean} smooth - Use smooth scrolling
   */
  const scrollToTop = useCallback((smooth = true) => {
    scrollToProgress(0, smooth);
  }, [scrollToProgress]);

  /**
   * Scroll to bottom
   * @param {boolean} smooth - Use smooth scrolling
   */
  const scrollToBottom = useCallback((smooth = true) => {
    scrollToProgress(100, smooth);
  }, [scrollToProgress]);

  /**
   * Reset progress tracking state
   */
  const resetProgress = useCallback(() => {
    setProgress(0);
    setScrollPosition(0);
    setHasReachedEnd(false);
    setIsScrolling(false);
    setScrollDirection(null);
    lastScrollTop.current = 0;
  }, []);

  /**
   * Get progress bar styles
   * @returns {Object} CSS styles for progress bar
   */
  const getProgressBarStyles = useCallback(() => {
    return {
      width: `${progress}%`,
      transition: isScrolling ? 'none' : 'width 0.3s ease-out',
    };
  }, [progress, isScrolling]);

  /**
   * Get progress as formatted string
   * @returns {string} Formatted progress (e.g., "45%")
   */
  const getFormattedProgress = useCallback(() => {
    return `${Math.round(progress)}%`;
  }, [progress]);

  /**
   * Check if user is near the top
   * @param {number} threshold - Threshold percentage (default: 5)
   * @returns {boolean} Is near top
   */
  const isNearTop = useCallback((threshold = 5) => {
    return progress <= threshold;
  }, [progress]);

  /**
   * Check if user is near the bottom
   * @param {number} threshold - Threshold percentage (default: 95)
   * @returns {boolean} Is near bottom
   */
  const isNearBottom = useCallback((threshold = 95) => {
    return progress >= threshold;
  }, [progress]);

  return {
    // State
    progress,
    scrollPosition,
    isScrolling,
    scrollDirection,
    hasReachedEnd,

    // Ref for custom container
    containerRef,

    // Actions
    scrollToProgress,
    scrollToTop,
    scrollToBottom,
    resetProgress,

    // Utilities
    getProgressBarStyles,
    getFormattedProgress,
    isNearTop,
    isNearBottom,
    calculateProgress,
  };
};

export default useReadingProgress;
